import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { ActionIcon, Badge, Group, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toApiError } from '../apollo/errors';
import { QueryState } from '../components/QueryState';
import { TablePager } from '../components/TablePager';
import {
  AdminMarkSupportMessageReadDocument,
  AdminSupportMessagesDocument,
} from '../graphql/operations';
import { useLabels } from '../labels';

const PAGE_SIZE = 20;

/**
 * "Write Us" (TAL-66/TAL-62): feedback typed in the app, read here instead of
 * an inbox nobody opens. There is no detail page — the message is the whole
 * row — and no way to un-read one: `adminMarkSupportMessageRead` only ever
 * moves a row from new to read, the same one-way status a real inbox's
 * "mark read" is.
 */
export function WriteUsPage() {
  const { t } = useTranslation();
  const labels = useLabels();
  const [page, setPage] = useState(1);

  const { data, loading, error } = useQuery(AdminSupportMessagesDocument, {
    variables: { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
  });

  const [markRead, { loading: marking }] = useMutation(AdminMarkSupportMessageReadDocument);

  function markAsRead(id: string) {
    void markRead({ variables: { id } }).catch((thrown: ErrorLike) => {
      notifications.show({
        color: 'red',
        title: t('writeUs.markReadFailed'),
        message: toApiError(thrown)?.message ?? '',
      });
    });
  }

  return (
    <Stack gap="md">
      <Title order={2}>{t('writeUs.title')}</Title>

      <QueryState loading={loading && !data} error={error}>
        {data && (
          <>
            <Table.ScrollContainer minWidth={640}>
              <Table highlightOnHover striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('writeUs.columns.user')}</Table.Th>
                    <Table.Th>{t('writeUs.columns.message')}</Table.Th>
                    <Table.Th>{t('writeUs.columns.sentAt')}</Table.Th>
                    <Table.Th>{t('writeUs.columns.status')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.adminSupportMessages.items.map((message) => (
                    <Table.Tr key={message.id}>
                      <Table.Td>
                        {message.user.email ?? <Text c="dimmed">{t('writeUs.noEmail')}</Text>}
                      </Table.Td>
                      <Table.Td>
                        <Text lineClamp={3} style={{ whiteSpace: 'pre-wrap' }}>
                          {message.text}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}>
                        {labels.dateTime(message.createdAt)}
                      </Table.Td>
                      <Table.Td>
                        {message.readAt ? (
                          <Badge variant="light" color="gray" size="sm">
                            {t('writeUs.read')}
                          </Badge>
                        ) : (
                          <Group gap="xs" wrap="nowrap">
                            <Badge variant="light" color="blue" size="sm">
                              {t('writeUs.unread')}
                            </Badge>
                            <Tooltip label={t('writeUs.markRead')}>
                              <ActionIcon
                                variant="light"
                                color="blue"
                                size="sm"
                                aria-label={t('writeUs.markRead')}
                                disabled={marking}
                                onClick={() => {
                                  markAsRead(message.id);
                                }}
                              >
                                <IconCheck size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {data.adminSupportMessages.items.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={4}>
                        <Text c="dimmed" ta="center" py="md">
                          {t('writeUs.empty')}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <TablePager
              total={data.adminSupportMessages.total}
              limit={PAGE_SIZE}
              page={page}
              onChange={setPage}
            />
          </>
        )}
      </QueryState>
    </Stack>
  );
}
